import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

export type MentionRef = {
  user_id: string;
  name: string | null;
};

export type WasteEventComment = {
  id: string;
  waste_event_id: string;
  org_id: string;
  author_id: string;
  body: string;
  created_at: string;
  updated_at: string;
  author_name: string | null;
  author_email: string | null;
  mentions: MentionRef[];
};

export type MentionableMember = {
  user_id: string;
  full_name: string | null;
  email: string | null;
};

/** Members the caller can @-mention on this event, in the event's site scope. */
export function useMentionableMembers(eventId: string | null) {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<MentionableMember[]>({
    queryKey: ["mentionable-members", eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/mentionable-members`, {
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: MentionableMember[] }).data;
    },
    enabled: !!eventId && !!headers.Authorization.replace("Bearer ", ""),
  });
}

/** The author may edit their own comment. */
export function canEditComment(comment: WasteEventComment, userId: string | null): boolean {
  return !!userId && comment.author_id === userId;
}

/** The author, or an org admin/platform admin, may delete a comment. */
export function canDeleteComment(
  comment: WasteEventComment,
  userId: string | null,
  isModerator: boolean,
): boolean {
  return isModerator || canEditComment(comment, userId);
}

export function useWasteEventComments(eventId: string | null) {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  return useQuery<WasteEventComment[]>({
    queryKey: ["waste-event-comments", eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/comments`, { headers });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: WasteEventComment[] };
      return body.data;
    },
    enabled: !!eventId && !!headers.Authorization.replace("Bearer ", ""),
  });
}

export function useCreateComment() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      body,
      mentions = [],
    }: {
      eventId: string;
      body: string;
      mentions?: string[];
    }): Promise<WasteEventComment> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/comments`, {
        method: "POST",
        headers,
        body: JSON.stringify({ body, mentions }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: WasteEventComment }).data;
    },
    onSuccess: (_data, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: ["waste-event-comments", eventId] });
      // Refresh the comment-count badge on the Transactions cards.
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}

export function useUpdateComment() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      commentId,
      body,
    }: {
      eventId: string;
      commentId: string;
      body: string;
    }): Promise<WasteEventComment> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/comments/${commentId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ body }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: WasteEventComment }).data;
    },
    onSuccess: (_data, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: ["waste-event-comments", eventId] });
    },
  });
}

export function useDeleteComment() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      eventId,
      commentId,
    }: {
      eventId: string;
      commentId: string;
    }): Promise<void> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/comments/${commentId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
    },
    onSuccess: (_data, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: ["waste-event-comments", eventId] });
      // Refresh the comment-count badge on the Transactions cards.
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}
